/**
 * The editing state behind the card-verification rows (#349).
 *
 * ONE controller, shared by the Extraction pane's rows and the full-screen
 * viewer's panel: the ticket's requirement is that a correction typed beside the
 * blown-up card and one typed in the pane are the SAME edit — one dirty state,
 * one save, one re-check. Two components each holding their own draft is how
 * that requirement gets broken quietly, so the draft lives here and the two
 * surfaces render it.
 *
 * What a row is measured against is WHAT THE CHECK ASKED, seeded by the host
 * from the head of the correction chain. A row holds one value and the question
 * staff are answering is "is this what we should be asking?", so the amber dot
 * means "this differs from the question we are currently putting" — which is
 * also what the backend compares against, so the screen and the server agree on
 * what counts as a change. The card read is the evidence beside it, and stands
 * in only where the check holds nothing.
 *
 * The payload is separate either way: only fields an operator actually TOUCHED
 * are sent. Sending every row whose read differs would apply the AI's reading as
 * though a human had confirmed it — that is the re-run sweep's job, under its
 * own #267 gate, not this screen's.
 */
import { useCallback, useMemo, useState } from 'react'
import {
  correctionPayload,
  sameFieldValue,
  withPayerPick,
  type CorrectableCheck,
  type CorrectableField,
  type CorrectionDraft,
  type PayerPick,
} from '../utils/checkCorrection'
import { correctAndRecheck, CorrectCheckUnrunnable } from '../services/correctCheck'
import type { CorrectCheckOutcome } from '../services/correctCheck'

/** What a surface needs to offer editing at all. Absent ⇒ rows render read-only
 *  (the staff console has no org claims, and view-mode staff may not write). */
export interface CardCorrectionInput {
  orgId: string
  check: CorrectableCheck
  /** Per-field values the check ASKED the payer (the chain head's identity) —
   *  the draft's baseline. Not the card read: see the header. */
  seed: Partial<Record<CorrectableField, string>>
  /** Fired once a corrected check exists; the host closes/refreshes. */
  onCorrected?: (outcome: Extract<CorrectCheckOutcome, { kind: 'fired' }>) => void
  canWrite?: boolean
  /**
   * The host has settled WHICH check this targets (#349). Until the chain
   * head is read, the target is the case's frozen original, and a press then
   * re-fires a check that may already be superseded — a second successor off
   * one source, two live answers for one visit. Neither typing nor firing is
   * allowed meanwhile: the first keystroke copies every field into the draft,
   * and a copy taken from the wrong check made the untouched fields read as
   * edits once the right one landed. Default true.
   */
  ready?: boolean
}

export interface CardCorrection {
  values: CorrectionDraft
  setValue: (field: CorrectableField, value: string) => void
  /**
   * A payer PICKED from the directory (#349): sets the name and remembers the
   * pick's ids, which make the payer authoritative on the backend — no clinic
   * alias, classifier or usage ranking runs on the name, so the new check
   * stores the name the operator chose and the row cannot read as edited
   * again once the seed catches up. Typing into the field afterwards drops
   * the ids: what is typed is a name, not a pick.
   */
  setPayer: (pick: PayerPick & { name: string }) => void
  /** Fields whose value differs from what the check asked. */
  dirtyFields: CorrectableField[]
  dirty: boolean
  isEdited: (field: CorrectableField) => boolean
  /** What the check asked for a field — the row title's "was …". */
  originalOf: (field: CorrectableField) => string
  saving: boolean
  /** True once a correction fired — the save button must not re-arm (a second
   *  press fires a second successor off one source; see CheckIdentityEditor). */
  saved: boolean
  error: string | null
  notice: string | null
  canWrite: boolean
  /** False while the host is still resolving the target — the rows stay
   *  read-only, Save and Re-check stay inert, and `save`/`recheck` are no-ops. */
  ready: boolean
  save: () => Promise<void>
  /**
   * Run the check again with nothing edited (#349 A6).
   *
   * Separate from `save` rather than a flag on it, because they are different
   * acts: one commits typed work, the other deliberately spends a payer call on
   * an unchanged question. Sharing an entry point would make "is anything
   * dirty?" the thing that decides whether a call is bought.
   */
  recheck: () => Promise<void>
  discard: () => void
}

const EMPTY_DRAFT: CorrectionDraft = {
  firstName: '', lastName: '', dateOfBirth: '', memberId: '', payerName: '',
}

/** The draft as the card left it — every field present, blank where unread. */
function draftFromSeed(seed: Partial<Record<CorrectableField, string>>): CorrectionDraft {
  return { ...EMPTY_DRAFT, ...seed }
}

export function useCardCorrection(input: CardCorrectionInput | null): CardCorrection | null {
  const seed = input?.seed
  const original = useMemo(() => draftFromSeed(seed ?? {}), [seed])
  const [draft, setDraft] = useState<CorrectionDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /** The directory entry behind the draft's payer name, when it was picked
   *  rather than typed — see `setPayer`. */
  const [payerPick, setPayerPick] = useState<PayerPick | null>(null)

  // A correction supersedes its source, so the host re-points us at the new
  // check once the flow reports it. That is a different question to correct:
  // the typed work is spent, and `saved` — which exists to stop a SECOND
  // successor being fired off ONE source — must release, or the panel would be
  // permanently read-only after the first correction. Adjusted during render
  // (this hook's own state, never a parent's) so no stale draft ever paints.
  const targetId = input?.check.id ?? null
  const [seenTarget, setSeenTarget] = useState(targetId)
  if (seenTarget !== targetId) {
    setSeenTarget(targetId)
    setSaved(false)
    setError(null)
    setNotice(null)
    // The DRAFT deliberately survives. It holds what we are now asking the
    // payer, and the new check's own values reach the seed one read later, when
    // the host re-resolves the chain head from the check it just created —
    // until they do, the draft is the only record of it on screen. It clears
    // itself the moment the seed catches up, because the two then agree and
    // nothing reads as edited.
  }

  // Null until something is typed, so a seed that moves (the chain head
  // resolving after open, a correction landing) still reaches an UNTOUCHED
  // panel. Once there is typed work the draft is authoritative — silently
  // swapping a value the operator is mid-correction on is the discard this
  // ticket exists to prevent.
  const values = draft ?? original

  const setValue = useCallback((field: CorrectableField, value: string) => {
    setDraft(prev => ({ ...(prev ?? original), [field]: value }))
    // A typed (or cleared) payer is no longer the entry that was picked; only
    // a fresh pick brings ids back.
    if (field === 'payerName') setPayerPick(null)
    setError(null)
    setNotice(null)
  }, [original])

  const setPayer = useCallback((pick: PayerPick & { name: string }) => {
    setDraft(prev => ({ ...(prev ?? original), payerName: pick.name }))
    setPayerPick({ tradingPartnerServiceId: pick.tradingPartnerServiceId, stediId: pick.stediId })
    setError(null)
    setNotice(null)
  }, [original])

  const dirtyFields = useMemo(
    () => (Object.keys(EMPTY_DRAFT) as CorrectableField[]).filter(
      f => values[f].trim() !== '' && !sameFieldValue(f, original[f], values[f]),
    ),
    [values, original],
  )

  const discard = useCallback(() => {
    setDraft(null)
    setPayerPick(null)
    setError(null)
    setNotice(null)
  }, [])

  const canWrite = !!input && input.canWrite !== false
  const ready = !!input && input.ready !== false
  const onCorrected = input?.onCorrected
  const orgId = input?.orgId
  const checkId = input?.check.id

  /** The one request path — `force` is the only thing the two callers differ by. */
  const run = useCallback(async (force: boolean) => {
    if (!orgId || !checkId || !canWrite || !ready || saving || saved) return
    const payload = withPayerPick(correctionPayload(original, values), payerPick)
    if (!force && Object.keys(payload).length === 0) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const outcome = await correctAndRecheck(orgId, checkId, payload, force)
      if (outcome.kind === 'no_change') {
        // Differs from the card read but asks the payer what it already asked —
        // say so rather than pretending a check ran.
        setNotice(outcome.message
          ?? 'Those values ask the payer the same question — no new check was needed.')
        return
      }
      setSaved(true)
      onCorrected?.(outcome)
    } catch (err) {
      setError(
        err instanceof CorrectCheckUnrunnable
          ? err.message
          : 'Could not re-run the check. Your edits are still here — try again.',
      )
    } finally {
      setSaving(false)
    }
  }, [orgId, checkId, canWrite, ready, saving, saved, original, values, payerPick, onCorrected])

  const save = useCallback(() => run(false), [run])
  const recheck = useCallback(() => run(true), [run])

  if (!input) return null
  return {
    values,
    setValue,
    setPayer,
    dirtyFields,
    dirty: dirtyFields.length > 0,
    isEdited: (f) => dirtyFields.includes(f),
    originalOf: (f) => original[f],
    saving,
    saved,
    error,
    notice,
    canWrite,
    ready,
    save,
    recheck,
    discard,
  }
}
